/**
 * 69.4 step 6c, the ground (the step 6 note's (3), BUILDLIST 69.4; DECISIONS 166 (4), the owner: "hmg in contested yes, but
 * fob only in owned territory"; 171 Q3/Q5 with the note's proposals as the defaults), with GameConfig.groundRules: a build
 * kind may stand only on ground of the kinds its row allows.
 * - **The ground** (`territory`) for a spot and a side: **owned**, **contested** or **enemy**.
 *   - Under Operations (a push or the tug of war) from 69.6f's sectors: the sector in play (the tug's live ones) is
 *     contested; one the side holds is owned; one the other side holds is enemy.
 *   - Without Operations, from the webs (171 Q3's default): owned within link range of a node of the side's linked web
 *     and of none of the other side's; enemy within the other's only; contested within both — and ground in neither web
 *     is no one's: contested. With no web (relays off) there is no rule.
 * - **The table** (`GROUND_RULES`): the HMG owned or contested; the FOB owned only; the relay owned or contested (one that
 *   carries drops, a spawn, owned only, 69.6f); the beacon owned only (69.6i loses it with its ground).
 * Asked by the build path's refusal (core/build.ts `buildRefusal`), so an order, the table and the commander's spot finder
 * meet it. 69.6f's spawn rule and the FOB's linked-only rule stand beside it. Pure; nothing here runs without the switch.
 */
import type { BuildKind, GameConfig, GameState, Vec3 } from '../types';
import { sectorAt } from './capturePoints';
import { sectorInPlay, sectorHolder } from './build';
import { linkedIds } from './web';
import { relayLink } from './relay';
import { arcDistance } from './gameState';

export type Ground = 'owned' | 'contested' | 'enemy';

/** Where each kind may stand (69.4 step 6c's table; 171 Q5: a relay in contested ground, one with drops owned only). */
export const GROUND_RULES: Record<BuildKind | 'hmg', readonly Ground[]> = {
  hmg: ['owned', 'contested'],
  fob: ['owned'],
  relay: ['owned', 'contested'],
  beacon: ['owned'],
};

/** Are the ground rules on in this match? */
export function groundRulesOn(config: Pick<GameConfig, 'groundRules'>): boolean {
  return !!config.groundRules;
}

/** The ground at `at` for `team`: under Operations from the sectors, else from the webs; null where there is no rule. */
export function territory(state: GameState, team: number, at: { planetIndex: number; surfaceDir: Vec3 }): Ground | null {
  const pts = state.modeState.points;
  if (pts?.front && pts.push && at.planetIndex === pts.front.planetIndex) {
    const k = sectorAt(pts.front, at.surfaceDir);
    if (sectorInPlay(state, k)) return 'contested';
    const holder = sectorHolder(state, k);
    return holder === null ? 'contested' : holder === team ? 'owned' : 'enemy';
  }
  if (!state.config.relays) return null;
  const planet = state.planets[at.planetIndex];
  if (!planet) return null;
  const reach = relayLink(state);
  const within = (t: number) => {
    const linked = new Set(state.webs?.[t]?.linked ?? linkedIds(state, t));
    return state.structures.some(s => linked.has(s.id) && s.health > 0 && s.planetIndex === at.planetIndex && arcDistance(s.surfaceDir, at.surfaceDir, planet.radius) <= reach);
  };
  const others = [...new Set(state.players.map(p => p.teamIndex))].filter(t => t !== team);
  const ours = within(team), theirs = others.some(within);
  return ours && !theirs ? 'owned' : theirs && !ours ? 'enemy' : 'contested';
}

/** Why `kind` may not stand at `at` for `team` by the ground (null when it may, or with the rules off). */
export function groundRefusal(state: GameState, kind: BuildKind | 'hmg', at: { planetIndex: number; surfaceDir: Vec3 }, team: number): string | null {
  if (!groundRulesOn(state.config)) return null;
  const ground = territory(state, team, at);
  if (ground === null) return null;
  const allowed = kind === 'relay' && state.config.drops ? (['owned'] as const) : GROUND_RULES[kind];
  if (allowed.includes(ground)) return null;
  return allowed.length === 1 ? 'Only on ground your side holds' : 'Not on ground the enemy holds';
}
